import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { ChevronDown, ExternalLink, Sparkles } from 'lucide-react';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import type { AssistantDisplayPayload } from '@charitystack/dashboard-assistant-contracts';
import type { DashboardAssistantDockMessage } from '../../../../contexts/DashboardAssistantDockContext';
import type { SuggestedNavigationPayload } from '../types';
import { getDashboardPathLabel } from '../lib/dashboardPathLabels';
import { getDockMessageKey } from '../lib/messageModel';
import {
  getActivityMessageDetails,
  getSafeDocumentationSources,
  isAudienceProposalPayload,
  isContactSelectionPreviewPayload,
  isContactTagsApplyResultPayload,
  isDashboardAnalyticsDisplayPayload,
  isDashboardSearchDisplayPayload,
  isDocumentationSourcesPayload,
  isSuggestedNavigationPayload,
} from '../lib/activityHelpers';
import { DashboardSearchResultsMessage } from './ToolResultMessages';
import { MarkdownMessage } from './MarkdownMessage';
import { DashboardAnalyticsResultMessage } from './DashboardAnalyticsResultMessage';
import { ContactSelectionPreviewCard } from './ContactSelectionPreviewCard';
import { ContactTagsApplyResultCard } from './ContactTagsApplyResultCard';
import AudienceProposalCard from './AudienceProposalCard';

export function DashboardActivityRow({ message }: { message: DashboardAssistantDockMessage }) {
  const detail = getActivityMessageDetails(message);
  const documentationSources = isDocumentationSourcesPayload(message.displayPayload)
    ? getSafeDocumentationSources(message.displayPayload)
    : [];

  return (
    <div className="flex max-w-full items-start gap-3 text-sm text-ink-muted">
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-ink-muted">
        {detail.icon}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm leading-5 text-ink-muted">{detail.label}</div>
        {detail.meta ? (
          <div className="mt-0.5 truncate text-xs leading-4 text-ink-muted/80">{detail.meta}</div>
        ) : null}
        {documentationSources.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {documentationSources.map((source, index) => (
              <a
                key={source.url}
                href={source.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-edge/80 bg-white px-2.5 py-1 text-[11px] font-medium leading-4 text-ink transition hover:border-sage/40 hover:bg-paper-light focus:outline-none focus:ring-2 focus:ring-sage/30"
                aria-label={`Source ${index + 1}: ${source.title}`}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-paper-inset text-[9px] font-semibold text-ink-muted">
                  {index + 1}
                </span>
                <span className="max-w-[220px] truncate">{source.title}</span>
                <ExternalLink className="h-3 w-3 shrink-0 text-ink-muted" />
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function DashboardActivityGroup({
  isComplete,
  messages,
}: {
  isComplete: boolean;
  messages: DashboardAssistantDockMessage[];
}) {
  const [isExpanded, setIsExpanded] = useState(!isComplete);
  const hasAutoCollapsedRef = useRef(isComplete);
  const hasLiveDashboardActivity = messages.some(
    (message) => message.metadata?.runtimeMode === 'live_browser',
  );
  const summaryDetail = getActivityMessageDetails(messages[messages.length - 1]);
  const activityCountLabel =
    messages.length === 1 ? '1 action' : `${messages.length} actions`;

  useEffect(() => {
    if (!isComplete) {
      hasAutoCollapsedRef.current = false;
      setIsExpanded(true);
      return;
    }

    if (hasAutoCollapsedRef.current) {
      return;
    }

    hasAutoCollapsedRef.current = true;
    setIsExpanded(false);
  }, [isComplete]);

  return (
    <div className="flex w-full justify-start">
      <div className="w-full py-1 text-sm leading-6 text-ink">
        <div className="border-l border-edge/80 pl-4">
          <button
            type="button"
            onClick={() => setIsExpanded((current) => !current)}
            className="mb-2 flex w-full max-w-full items-center gap-2 text-left text-xs font-medium uppercase tracking-wide text-ink-muted transition hover:text-ink"
            aria-expanded={isExpanded}
          >
            <motion.span
              animate={{ rotate: isExpanded ? 0 : -90 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              className="flex h-4 w-4 shrink-0 items-center justify-center"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </motion.span>
            <Sparkles className="h-3.5 w-3.5 shrink-0" />
            <span className="shrink-0">Activity</span>
            {hasLiveDashboardActivity ? (
              <span className="shrink-0 normal-case tracking-normal text-ink-muted/80">
                Live dashboard
              </span>
            ) : null}
            {!isExpanded ? (
              <span className="min-w-0 truncate normal-case tracking-normal text-ink-muted/80">
                {activityCountLabel}
                {summaryDetail.label ? ` · ${summaryDetail.label}` : ''}
              </span>
            ) : null}
          </button>
          <AnimatePresence initial={false}>
            {isExpanded ? (
              <motion.div
                className="space-y-2 overflow-hidden"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                {messages.map((message) => (
                  <DashboardActivityRow key={getDockMessageKey(message)} message={message} />
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export function SuggestedNavigationMessage({ payload }: { payload: SuggestedNavigationPayload }) {
  const history = useHistory();
  const path = typeof payload.data.path === 'string' ? payload.data.path : '';
  const label =
    typeof payload.data.label === 'string' && payload.data.label.trim()
      ? payload.data.label
      : payload.summary || getDashboardPathLabel(path) || 'Open page';
  const prefix = payload.renderer === 'dashboard_navigation' ? 'Opened' : 'Suggested page';

  if (!path) {
    return (
      <div className="flex w-full justify-start">
        <div className="rounded-full bg-paper-light px-2.5 py-1 text-[11px] leading-5 text-ink-muted">
          {payload.summary || 'Suggested a dashboard page'}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full justify-start">
      <button
        onClick={() => history.push(path)}
        className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-paper-light px-2.5 py-1 text-left text-[11px] leading-5 text-ink-muted transition hover:bg-edge/40 hover:text-ink"
        type="button"
      >
        <span className="truncate">
          {prefix}: <span className="font-medium text-ink">{label}</span>
        </span>
        <ArrowUpRightIcon className="h-3 w-3 shrink-0" />
      </button>
    </div>
  );
}

export function AssistantMessageContent({
  allowedDocumentationUrls = [],
  animateText = false,
  content,
  dashboardTimezone,
  merchantCountryCode,
  displayPayload,
  isStreaming = false,
  onAnimationComplete,
  onUseList,
  threadId,
  useListDisabled,
}: {
  allowedDocumentationUrls?: string[];
  animateText?: boolean;
  content: string;
  dashboardTimezone?: string | null;
  merchantCountryCode?: string | null;
  displayPayload?: AssistantDisplayPayload;
  isStreaming?: boolean;
  onAnimationComplete?: () => void;
  onUseList?: (message: string) => Promise<boolean>;
  threadId: string;
  useListDisabled?: boolean;
}) {
  if (isSuggestedNavigationPayload(displayPayload)) {
    return <SuggestedNavigationMessage payload={displayPayload} />;
  }

  if (isDashboardSearchDisplayPayload(displayPayload)) {
    return (
      <DashboardSearchResultsMessage
        dashboardTimezone={dashboardTimezone}
        payload={displayPayload.data}
      />
    );
  }

  if (isDashboardAnalyticsDisplayPayload(displayPayload)) {
    return <DashboardAnalyticsResultMessage payload={displayPayload.data} />;
  }

  if (isContactSelectionPreviewPayload(displayPayload)) {
    return (
      <ContactSelectionPreviewCard
        payload={displayPayload.data}
        threadId={threadId}
        onUseList={onUseList}
        useListDisabled={useListDisabled}
      />
    );
  }

  if (isContactTagsApplyResultPayload(displayPayload)) {
    return <ContactTagsApplyResultCard payload={displayPayload.data} />;
  }

  if (isAudienceProposalPayload(displayPayload)) {
    return <AudienceProposalCard value={displayPayload.data} merchantCountryCode={merchantCountryCode} />;
  }

  return (
    <MarkdownMessage
      allowedDocumentationUrls={allowedDocumentationUrls}
      animateText={animateText}
      content={content}
      isStreaming={isStreaming}
      onAnimationComplete={onAnimationComplete}
    />
  );
}

export function DashboardAssistantThinkingIndicator() {
  return (
    <div className="flex justify-start" aria-live="polite" aria-label="Assistant is thinking">
      <div className="px-1 py-1 text-sm leading-6 text-ink-muted">
        <motion.span
          animate={{ opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          Thinking
        </motion.span>
      </div>
    </div>
  );
}
